// ImmunizationHepBViewNew.jsx
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./ImmunizationHepBViewNew.module.css";

import { AddButton, InputBox, TextArea } from "@cpnw/ui";
import DashboardRadio from "components/DashboardRadio";
import FormDatePicker from "components/FormDatePicker";
import classNames from "classnames";
import { DATE_FORMAT_PATTERN } from "pages/enums";
// import DashboardPassportFileEditorItem from "components/DashboardPassportFileEditorItem";
// import GenericFileUpload from "./GenericFileUpload";

/* --- Constants --- */
const TiterOptions = {
  TITER: "TITER", // Proof of Immunity (POSITIVE ONLY)
  NO_TITER: "NO_TITER", // I do not have a titer or my titer is negative
};

const NoTiterReasonOptions = {
  nonConverter: "nonConverter",
  seriesInProcess: "seriesInProcess",
  historyOfDisease: "historyOfDisease",
  healthReason: "healthReason",
};

const ReviewerDeclination = {
  NON_CONVERTER: "NON_CONVERTER",
  SERIES_IN_PROCESS: "SERIES_IN_PROCESS",
  DECLINATION_FORM: "DECLINATION_FORM",
};

const HepBSubmissionTypes = {
  proofOfImmunity: "proofOfImmunity",
  seriesInProcess: "seriesInProcess",
  historyOfDisease: "historyOfDisease",
  nonConverter: "nonConverter",
};

const HepBTestResults = {
  Neg: "Neg",
  Pos: "Pos",
};

const Series = {
  TwoStep: "2-Step",
  ThreeStep: "3-Step",
};

const templateData = {
  type: null, // "TITER" | "NO_TITER"

  // TITER option
  proofOfImmunity: {
    proofs: [],      // [{ date: null, result: null }]
    files: [],       // Existing uploaded files from server
    newFiles: [],
    newFileNames: []
  },

  // NO_TITER option
  noTiterReason: null, // "NON_CONVERTER" | "SERIES_IN_PROCESS" | "HISTORY_OF_DISEASE" | "HEALTH_REASON"

  // NON_CONVERTER series
  nonConverter: {
    serie1: {
      type: null,           // "2-Step" | "3-Step"
      twoStepDate1: null,
      twoStepDate2: null,
      twoStepDateDrawn: null,
      twoStepResult: null,  // "Pos" | "Neg"
      threeStepDate1: null,
      threeStepDate2: null,
      threeStepDate3: null,
      threeStepDateDrawn: null,
      threeStepResult: null // "Pos" | "Neg"
    },
    serie2: {
      type: null,
      twoStepDate1: null,
      twoStepDate2: null,
      twoStepDateDrawn: null,
      twoStepResult: null,
      threeStepDate1: null,
      threeStepDate2: null,
      threeStepDate3: null,
      threeStepDateDrawn: null,
      threeStepResult: null
    },
    files: [],       // Existing uploaded files from server
    newFiles: [],
    newFileNames: []
  },

  // SERIES_IN_PROCESS
  seriesInProcess: {
    processDate: { date: null },
    serie1: {
        type: null,
        twoStepDate1: null,
        twoStepDate2: null,
        twoStepDateDrawn: null,
        twoStepResult: null,

        threeStepDate1: null,
        threeStepDate2: null,
        threeStepDate3: null,
        threeStepDateDrawn: null,
        threeStepResult: null,
    },
    files: [],       // Existing uploaded files from server
    newFiles: [],
    newFileNames: [],
    form: "https://cpnw.org/forms/series-in-process.pdf"
  },

  // HISTORY_OF_DISEASE
  historyOfDisease: {
    files: [],       // Existing uploaded files from server
    newFiles: [],
    newFileNames: []
  },

  // HEALTH_REASON
  healthReasonText: "",
  healthReasonTextChanged: false,

  // Optional reviewer-only fields
  reviewer: {
    declination: "",
    files: [],       // Existing uploaded files from server
    newFiles: [],
    newFileNames: []
  }
};

const sanitizeData = (data, defaultData) => {
  if (!data || !defaultData) return;

  Object.keys(data).forEach((key) => {
    if (!(key in defaultData)) {
      // Delete key if it doesn't exist in defaultData
      delete data[key];
    } else {
      // If it's an object, recurse
      if (
        data[key] !== null &&
        typeof data[key] === "object" &&
        !Array.isArray(data[key])
      ) {
        sanitizeData(data[key], defaultData[key]);
      }
    }
  });
};

/* --- Main Component --- */
export const ImmunizationHepBViewNew = forwardRef(
  ({ data = {}, onChange, disabled = false, onNotify = null, roles = [] }, ref) => {
    const [healthText, setHealthText] = useState("");
    const prevHealthReasonText = useRef("");
    const initialHealthReasonText = useRef(data.healthReasonText || "");
    const fileRef = useRef(null);
    const canEdit = !disabled;
    const [fieldErrors, setFieldErrors] = useState({});

    const [files, setFiles] = useState([]);
    const [newFiles, setNewFiles] = useState([]);

    const setValue = () => {
      if (onChange) onChange({ ...data });
    };

    const canDeleteFile = () => {
      return true;
    }

    // Initialize on load
    useEffect(() => {
      prevHealthReasonText.current = data.healthReasonText || "";
      setHealthText(data.healthReasonText || "");
    }, []); // run only on mount


    useImperativeHandle(ref, () => ({
      errors: () => fieldErrors,
      onSaveComplete: async () => {
        const isHealthReason = data.noTiterReason === NoTiterReasonOptions.healthReason;
        const hasChanged = prevHealthReasonText.current !== healthText;

        if (roles.includes("STUDENT") && isHealthReason && hasChanged) {
          onNotify();

          // Update previous value so we don’t notify again
          prevHealthReasonText.current = healthText;
        }

      },
      validator: () => {
        const errors = {};

        if (data.type == TiterOptions.TITER) {
          // Remove NO_TITER related fields
          delete data.noTiterReason;
          delete data.nonConverter;
          delete data.seriesInProcess;
          delete data.historyOfDisease;
          delete data.healthReasonText;
          delete data.healthReasonTextChanged;

          // Ensure proofOfImmunity exists
          if (!data.proofOfImmunity) data.proofOfImmunity = { proofs: [], files: [], newFiles: [], newFileNames: [] };

          // Enforce POSITIVE ONLY
          (data.proofOfImmunity.proofs || []).forEach((p) => {
            p.result = HepBTestResults.Pos;
          });

          // Notify parent
          setValue();
        }
        if (data.type == TiterOptions.NO_TITER) {
          // Remove TITER related fields
          delete data.proofOfImmunity;

          // Ensure noTiterReason exists
          if (!data.noTiterReason) data.noTiterReason = null;

          // Cleanup irrelevant properties
          switch (data.noTiterReason) {
            case NoTiterReasonOptions.nonConverter:
              delete data.seriesInProcess;
              delete data.historyOfDisease;
              delete data.healthReasonText;
              delete data.healthReasonTextChanged;

              // --- Handle series cleanup for Non-Converter ---
              ["serie1", "serie2"].forEach((serieKey) => {
                const serie = data.nonConverter?.[serieKey];
                if (serie) {
                  if (serie.type === Series.TwoStep) {
                    delete serie.threeStepDate1;
                    delete serie.threeStepDate2;
                    delete serie.threeStepDate3;
                    delete serie.threeStepDateDrawn;
                    delete serie.threeStepResult;
                  } else if (serie.type === Series.ThreeStep) {
                    delete serie.twoStepDate1;
                    delete serie.twoStepDate2;
                    delete serie.twoStepDateDrawn;
                    delete serie.twoStepResult;
                  }
                }
              });
              break;

            case NoTiterReasonOptions.seriesInProcess:
              delete data.nonConverter;
              delete data.historyOfDisease;
              delete data.healthReasonText;
              delete data.healthReasonTextChanged;
              break;

            case NoTiterReasonOptions.historyOfDisease:
              delete data.nonConverter;
              delete data.seriesInProcess;
              delete data.healthReasonText;
              delete data.healthReasonTextChanged;
              break;

            case NoTiterReasonOptions.healthReason:
              delete data.nonConverter;
              delete data.seriesInProcess;
              delete data.historyOfDisease;

              if (!data.healthReasonText || data.healthReasonText.trim() === "") {
                errors.healthReasonText = "Please explain the health reason.";
              }

              break;

            default:
              break;
          }
        }

        // --- Set errors to state ---
        setFieldErrors(errors);

        // --- Stop save if any errors ---
        if (Object.keys(errors).length > 0) {
          return errors; // stop save
        }

        // --- If valid, sanitize data and notify parent ---
        sanitizeData(data, templateData);
        setValue();
        return {};
      },

    }));

    const ensureTypePath = (type) => {
      if (!data[type]) data[type] = {};
    };

    /**
 * Generic file handlers for any section:
 * Example: fileHandlers("nonConverter"), fileHandlers("historyOfDisease"), etc.
 */
    const fileHandlers = (key) => {
      const ensure = () => {
        if (!data[key]) {
          // If the section doesn't exist, clone entire template section
          data[key] = JSON.parse(JSON.stringify(templateData[key]));
        }

        // Ensure required file arrays exist even if section already existed
        if (!Array.isArray(data[key].files)) data[key].files = [];
        if (!Array.isArray(data[key].newFiles)) data[key].newFiles = [];
        if (!Array.isArray(data[key].newFileNames)) data[key].newFileNames = [];
      };

      return {
        files: () => (Array.isArray(data[key]?.files) ? data[key].files : []),
        newFiles: () => (Array.isArray(data[key]?.newFiles) ? data[key].newFiles : []),

        add: (added) => {
          ensure();
          data[key].newFiles = [...data[key].newFiles, ...added];
          // Extract file names correctly
          const names = added.map(f => f.name);
          data[key].newFileNames = [...data[key].newFileNames, ...names];
          setValue();
        },

        deleteExisting: (file) => {
          ensure();
          const id = file.id || file.Id;
          data[key].files = data[key].files.filter(
            (f) => (f.id || f.Id) !== id
          );
          // Remove file name
          data[key].newFileNames = data[key].newFileNames.filter(
            (name) => name !== (file.name || file.Name)
          );
          setValue();
        },

        deleteNew: (file) => {
          ensure();
          const id = file.id || file.Id;
          data[key].newFiles = data[key].newFiles.filter(
            (f) => (f.id || f.Id) !== id
          );
          // Remove file name
          data[key].newFileNames = data[key].newFileNames.filter(
            (name) => name !== (file.name || file.Name)
          );
          setValue();
        },
      };
    };


    const cloneFromTemplate = (key) => {
      const section = templateData[key];
      return JSON.parse(JSON.stringify(section));
    };

    const proofOfImmunityFiles = fileHandlers(HepBSubmissionTypes.proofOfImmunity);
    const nonConverterFiles = fileHandlers(HepBSubmissionTypes.nonConverter);
    const historyOfDiseaseFiles = fileHandlers(HepBSubmissionTypes.historyOfDisease);
    const seriesInProcessFiles = fileHandlers(HepBSubmissionTypes.seriesInProcess);

    return (
      <div className={classNames(styles.container, styles.lv0)}>
        <div className={styles.section}>
          <div className={styles.radioGroup}>
            {/* --- Titer Option --- */}
            <DashboardRadio
              name="hep-titer-yes"
              isSelected={data.type === TiterOptions.TITER}
              onChange={() => {
                data.type = TiterOptions.TITER;
                delete data.noTiterReason;
                setValue();
              }}
              value={TiterOptions.TITER}
              label="Proof of immunity by Titer (POSITIVE ONLY)"
              containerClassName={styles.radioContainer}
              disabled={disabled}
            />
            {data.type === TiterOptions.TITER && (
              <div className={classNames(styles.testGroup)}>
                <TypeBContent
                  typeKey="proofOfImmunity"
                  field="proofs"
                  label="Date: "
                  data={data}
                  updateEntry={(field, index, key, value) => {
                    ensureTypePath("proofOfImmunity");
                    if (!data.proofOfImmunity[field]) data.proofOfImmunity[field] = [];
                    if (!data.proofOfImmunity[field][index]) data.proofOfImmunity[field][index] = {};
                    data.proofOfImmunity[field][index][key] = value;
                    setValue();
                  }}
                  addEntry={(field) => {
                    ensureTypePath("proofOfImmunity");
                    if (!data.proofOfImmunity[field]) data.proofOfImmunity[field] = [];
                    data.proofOfImmunity[field].push({ date: null, result: null });
                    setValue();
                  }}
                  removeEntry={(field, index) => {
                    if (data.proofOfImmunity?.[field]) {
                      data.proofOfImmunity[field].splice(index, 1);
                      setValue();
                    }
                  }}
                  disabled={disabled}
                />
                {/* <GenericFileUpload
                  label="Upload Provider Documentation"
                  disabled={disabled}
                  files={proofOfImmunityFiles.files()}
                  newFiles={proofOfImmunityFiles.newFiles()}
                  onAddFiles={proofOfImmunityFiles.add}
                  onDeleteExisting={proofOfImmunityFiles.deleteExisting}
                  onDeleteNew={proofOfImmunityFiles.deleteNew}
                /> */}
                <div style={{ margin: '10px 0 10px'}}>
                  Upload Provider Documentation Below
                </div>
              </div>
            )}
            <DashboardRadio
              name="hep-titer-no"
              isSelected={data.type === TiterOptions.NO_TITER}
              onChange={() => {
                data.type = TiterOptions.NO_TITER;
                data.noTiterReason = data.noTiterReason || null;
                setValue();
              }}
              value={TiterOptions.NO_TITER}
              label="I do not have a titer, or my titer result is negative"
              containerClassName={styles.radioContainer}
              disabled={disabled}
            />
            {data.type === TiterOptions.NO_TITER && (
              <div className={classNames(styles.testGroup)}>
                <DashboardRadio
                  name="no-titer-nonconverter"
                  isSelected={data.noTiterReason === NoTiterReasonOptions.nonConverter}
                  onChange={() => {
                    data.noTiterReason = NoTiterReasonOptions.nonConverter;
                    setValue();
                  }}
                  value={NoTiterReasonOptions.nonConverter}
                  label="Non-Converter"
                  containerClassName={styles.radioContainer}
                  disabled={disabled}
                />
                {data.noTiterReason === NoTiterReasonOptions.nonConverter && (
                  <>
                    <div className={classNames(styles.testGroup, styles.lv1)}>
                      <b>Series #1</b>
                      <SeriesContent
                        typeKey={NoTiterReasonOptions.nonConverter}
                        serieKey="serie1"
                        data={data}
                        setValue={setValue}
                        disabled={disabled}
                      />
                      <div style={{ margin: "10px 0" }}>
                        If titer is negative after series #1, then include series #2 information with date the titer was drawn and results.
                      </div>
                      <b>Series #2</b>
                      <SeriesContent
                        typeKey={NoTiterReasonOptions.nonConverter}
                        serieKey="serie2"
                        data={data}
                        setValue={setValue}
                        disabled={disabled}
                      />
                    </div>
                    {/* <GenericFileUpload
                      label="Upload Provider Documentation"
                      disabled={disabled}
                      files={nonConverterFiles.files()}
                      newFiles={nonConverterFiles.newFiles()}
                      onAddFiles={nonConverterFiles.add}
                      onDeleteExisting={nonConverterFiles.deleteExisting}
                      onDeleteNew={nonConverterFiles.deleteNew}
                    /> */}
                    <div style={{ margin: '10px 0 10px' }}>
                      Upload Provider Documentation Below
                    </div>
                  </>
                )}
                <DashboardRadio
                  name="no-titer-series-process"
                  isSelected={data.noTiterReason === NoTiterReasonOptions.seriesInProcess}
                  onChange={() => {
                    data.noTiterReason = NoTiterReasonOptions.seriesInProcess;
                    setValue();
                  }}
                  value={NoTiterReasonOptions.seriesInProcess}
                  label="Series in Process"
                  containerClassName={styles.radioContainer}
                  disabled={disabled}
                />
                {data.noTiterReason === NoTiterReasonOptions.seriesInProcess && (
                  <div className={classNames(styles.testGroup, styles.lv1)}>
                    <SeriesContent
                      typeKey={NoTiterReasonOptions.seriesInProcess}
                      serieKey="serie1"
                      data={data}
                      setValue={setValue}
                      disabled={disabled}
                    />
                    {/* <div style={{ marginTop: 8 }}>
                      <a href={templateData.seriesInProcess.form}
                        target='_blank' rel="noopener noreferrer" className={styles.link_item}>Download Series in Process Form</a>
                    </div> */}
                    {/* <div style={{ marginTop: 8 }}>
                      <GenericFileUpload
                        label="Upload Series in Process Form"
                        disabled={disabled}
                        files={seriesInProcessFiles.files()}
                        newFiles={seriesInProcessFiles.newFiles()}
                        onAddFiles={seriesInProcessFiles.add}
                        onDeleteExisting={seriesInProcessFiles.deleteExisting}
                        onDeleteNew={seriesInProcessFiles.deleteNew}
                      />
                    </div> */}
                  </div>
                )}
                <DashboardRadio
                  name="no-titer-history"
                  isSelected={data.noTiterReason === NoTiterReasonOptions.historyOfDisease}
                  onChange={() => {
                    data.noTiterReason = NoTiterReasonOptions.historyOfDisease;
                    setValue();
                  }}
                  value={NoTiterReasonOptions.historyOfDisease}
                  label="History of Disease"
                  containerClassName={styles.radioContainer}
                  disabled={disabled}
                />
                {data.noTiterReason === NoTiterReasonOptions.historyOfDisease && (
                  // <GenericFileUpload
                  //   label="Upload Provider Documentation"
                  //   disabled={disabled}
                  //   files={historyOfDiseaseFiles.files()}
                  //   newFiles={historyOfDiseaseFiles.newFiles()}
                  //   onAddFiles={historyOfDiseaseFiles.add}
                  //   onDeleteExisting={historyOfDiseaseFiles.deleteExisting}
                  //   onDeleteNew={historyOfDiseaseFiles.deleteNew}
                  // />
                  <div style={{ margin: '10px 0 10px' }}>
                    Upload Provider Documentation Below
                  </div>
                )}
                <DashboardRadio
                  name="no-titer-health-reason"
                  isSelected={data.noTiterReason === NoTiterReasonOptions.healthReason}
                  onChange={() => {
                    data.noTiterReason = NoTiterReasonOptions.healthReason;
                    setValue();
                  }}
                  value={NoTiterReasonOptions.healthReason}
                  label="Health Reason"
                  containerClassName={styles.radioContainer}
                  disabled={disabled}
                />
                {data.noTiterReason === NoTiterReasonOptions.healthReason && (
                  <div className={classNames(styles.testGroup, styles.lv1)}>
                    <label>
                      Explain the health reason
                      <span style={{ marginLeft: 8, fontSize: 12, color: "#666" }}>
                        ({healthText.length}/200)
                      </span>
                    </label>
                    <textarea
                      maxLength={200}
                      value={healthText}
                      onChange={(e) => {
                        const newValue = e.target.value;

                        // Update state only
                        setHealthText(newValue);

                        // Update data object if needed
                        data.healthReasonText = newValue;

                        // Compare with original
                        const hasChanged =
                          initialHealthReasonText.current.trim() !== newValue.trim();

                        // Set your new flag
                        data.healthReasonTextChanged = hasChanged;

                        setValue(); // update parent
                      }}
                      readOnly={roles.includes("HEALTHCARE")}
                      className={styles.healthReasonTextarea}
                    />
                    {fieldErrors.healthReasonText && (
                      <div className={styles.errorMessage}>{fieldErrors.healthReasonText}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ImmunizationHepBViewNew.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  isReviewer: PropTypes.bool,
  onNotify: PropTypes.func,
};

/* --- Sub-components (SeriesContent, TypeBContent, TypeCContent, LabeledDatePicker, ResultRadioGroup) remain unchanged --- */

function SeriesContent({ typeKey, serieKey, data, setValue, disabled }) {
  const serie = data?.[typeKey]?.[serieKey] || {};

  const ensurePath = () => {
    if (!data[typeKey]) data[typeKey] = {};
    if (!data[typeKey][serieKey]) data[typeKey][serieKey] = {};
  };

  const handleSet = (field, value) => {
    ensurePath();
    data[typeKey][serieKey][field] = value;
    setValue();
  };

  const handleTypeChange = (value) => {
    ensurePath();
    const existing = data[typeKey][serieKey] || {};

    data[typeKey][serieKey] = {
      ...existing,
      type: value,
    };

    setValue();
  };

  return (
    <div className={classNames(styles.testGroup, styles.lv1)}>
      <DashboardRadio
        name={`${serieKey}-series-type`}
        isSelected={serie.type === Series.TwoStep}
        onChange={() => handleTypeChange(Series.TwoStep)}
        value={Series.TwoStep}
        label={Series.TwoStep}
        containerClassName={styles.radioContainer}
        disabled={disabled}
      />
      {serie.type === Series.TwoStep && (
        <div className={classNames(styles.testColumn, styles.lv2)}>
          <span>Vaccination Dates</span>
          {[1, 2].map((i) => (
            <div className={classNames(styles.testRow, styles.lv3)} key={i}>
              <LabeledDatePicker
                label={`${i}.`}
                dateString={serie[`twoStepDate${i}`]}
                onChange={(value) => handleSet(`twoStepDate${i}`, value)}
                disabled={disabled}
              />
            </div>
          ))}
          <span>Titer</span>
          <div className={classNames(styles.testRow, styles.lv3)}>
            <div className={classNames(styles.testItem)}>
              <LabeledDatePicker
                label="Date Drawn:"
                dateString={serie.twoStepDateDrawn}
                onChange={(value) => handleSet("twoStepDateDrawn", value)}
                disabled={disabled}
              />
            </div>
            <div style={{ margin: "0px 10px" }}> </div>
            <div className={classNames(styles.testItem)}>
              <ResultRadioGroup
                name={`${serieKey}-two-step-titer-result`}
                value={serie.twoStepResult}
                onChange={(val) => handleSet("twoStepResult", val)}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
      <DashboardRadio
        name={`${serieKey}-series-type`}
        isSelected={serie.type === Series.ThreeStep}
        onChange={() => handleTypeChange(Series.ThreeStep)}
        value={Series.ThreeStep}
        label={Series.ThreeStep}
        containerClassName={styles.radioContainer}
        disabled={disabled}
      />
      {serie.type === Series.ThreeStep && (
        <div className={classNames(styles.testColumn, styles.lv2)}>
          <span>Vaccination Dates</span>
          {[1, 2, 3].map((i) => (
            <div className={classNames(styles.testRow, styles.lv3)} key={i}>
              <LabeledDatePicker
                label={`${i}.`}
                dateString={serie[`threeStepDate${i}`]}
                onChange={(value) => handleSet(`threeStepDate${i}`, value)}
                disabled={disabled}
              />
            </div>
          ))}
          <span>Titer</span>
          <div className={classNames(styles.testRow, styles.lv3)}>
            <div className={classNames(styles.testItem)}>
              <LabeledDatePicker
                label="Date Drawn:"
                dateString={serie.threeStepDateDrawn}
                onChange={(value) => handleSet("threeStepDateDrawn", value)}
                disabled={disabled}
              />
            </div>
            <div style={{ margin: "0px 10px" }}> </div>
            <div className={classNames(styles.testItem)}>
              <ResultRadioGroup
                name={`${serieKey}-three-step-titer-result`}
                value={serie.threeStepResult}
                onChange={(val) => handleSet("threeStepResult", val)}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TypeBContent({ field, label, data, typeKey, updateEntry, addEntry, removeEntry, disabled }) {
  const ALLOW_ADD_PROOF = false;
  const proofList = data[typeKey]?.[field] || [];

  // Initialize first proof if list is empty
  useEffect(() => {
    if (!disabled && proofList.length === 0) {
      addEntry(field);
    }
  }, [proofList, field, addEntry, disabled]);

  useEffect(() => {
    proofList.forEach((entry, index) => {
      if (!entry?.result) {
        updateEntry(field, index, "result", HepBTestResults.Pos);
      }
    });

  }, [proofList]);

  return (
    <div>
      {proofList.map((entry, index) => (
        <div key={index} className={styles.testColumn}>
          <div key={index} className={styles.testRow}>
            <div className={classNames(styles.testItem)}>
              <LabeledDatePicker
                label={label}
                dateString={entry.date}
                onChange={(value) => updateEntry(field, index, "date", value)}
                disabled={disabled}
              />
            </div>
            <div className={classNames(styles.testItem, styles.lv2)}>
              <ResultRadioGroup
                name={`${field}-result-${index}`}
                value={entry.result}
                onChange={(val) => updateEntry(field, index, "result", val)}
                disabled={disabled}
                allowedResults={[HepBTestResults.Pos]}
              />
              {!disabled && ALLOW_ADD_PROOF && (
                <button onClick={() => removeEntry(field, index)} aria-label="Remove">x</button>
              )}
            </div>
          </div>
        </div>
      ))}
      {!disabled && ALLOW_ADD_PROOF && (
        <AddButton onClick={() => addEntry(field)} label="Add Proof" />
      )}
    </div>
  );
}

function TypeCContent({ typeKey, field, label, data, updateEntry, disabled }) {
  const value = data[typeKey]?.[field]?.date || null;

  return (
    <div>
      <div className={classNames(styles.testRow)}>
        <LabeledDatePicker
          label={label}
          dateString={value}
          onChange={(value) => updateEntry(field, "date", value)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function LabeledDatePicker({ label, dateString, onChange, disabled }) {
  return (
    <div className={styles.datePickerContainer}>
      <span>{label}</span>
      <FormDatePicker
        dateFormat={DATE_FORMAT_PATTERN}
        dateString={dateString}
        setDate={onChange}
        disabled={disabled}
      />
    </div>
  );
}

function ResultRadioGroup({ name, value, onChange, disabled, allowedResults }) {
  const results = allowedResults?.length ? allowedResults : Object.values(HepBTestResults);

  return (
    <div className={classNames(styles.resultContainer)} style={{ marginLeft: 10 }}>
      <span>Result:</span>
      {results.map((result) => (
        <div key={result} className={classNames(styles.radios)}>
          <DashboardRadio
            name={name}
            isSelected={value === result}
            onChange={() => onChange(result)}
            value={result}
            label={result}
            containerClassName={styles.radioContainer}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
}